import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Receipt, Download, Send, ExternalLink, Loader2, RefreshCw,
  Search, Calendar, FileText, TrendingUp, FileDown, ShoppingCart, Mail,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import PurchasesSubTab from "./PurchasesSubTab";
import { API } from "../../App";

// Small inline classifier for costed-but-unclassified units. Surfaces the
// count + inline dropdowns to bulk-classify each product group at 23%.
const UnclassifiedVatBanner = () => {
  const [data, setData] = React.useState(null);
  const [selection, setSelection] = React.useState({}); // product_name → treatment
  const [applyingProduct, setApplyingProduct] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stock/units/without-vat`);
      setData(res.data);
    } catch (e) { /* silent — banner just hides */ }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const apply = async (product_name) => {
    const treatment = selection[product_name] || "standard";
    setApplyingProduct(product_name);
    try {
      const res = await axios.post(`${API}/stock/units/bulk-vat-backfill`,
        { product_name, vat_treatment: treatment });
      toast.success(`Classified ${res.data.modified_count} × ${product_name} as ${treatment}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "VAT backfill failed");
    } finally {
      setApplyingProduct(null);
    }
  };

  if (!data || data.count === 0) return null;
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4"
         data-testid="unclassified-vat-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-amber-400 font-semibold text-sm">
            {data.count} costed unit{data.count === 1 ? "" : "s"} awaiting VAT classification
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            These units have a cost but no VAT treatment set. Excluded from
            purchases-VAT totals until classified. Classifier applies at{" "}
            {((data.purchases_vat_rate || 0.23) * 100).toFixed(0)}% for standard treatment.
          </div>
        </div>
      </div>
      <div className="mt-3 max-h-64 overflow-y-auto space-y-1.5">
        {data.by_product.slice(0, 20).map(({ product_name, count }) => (
          <div key={product_name} className="grid grid-cols-12 gap-2 items-center text-xs"
               data-testid={`unclassified-row-${product_name}`}>
            <div className="col-span-6 text-white truncate">
              {product_name} <span className="text-slate-500">× {count}</span>
            </div>
            <div className="col-span-4">
              <select
                value={selection[product_name] || "standard"}
                onChange={(e) => setSelection((p) => ({ ...p, [product_name]: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 text-white text-xs rounded px-2 py-1 h-7"
                data-testid={`unclassified-treatment-${product_name}`}
              >
                <option value="standard">Standard (23%)</option>
                <option value="eu_reverse_charge">EU reverse charge</option>
                <option value="no_vat">No VAT</option>
              </select>
            </div>
            <div className="col-span-2">
              <Button
                size="sm"
                onClick={() => apply(product_name)}
                disabled={applyingProduct === product_name}
                className="bg-amber-600 hover:bg-amber-700 text-white h-7 w-full text-xs"
                data-testid={`unclassified-apply-${product_name}`}
              >
                {applyingProduct === product_name
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : "Classify"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const currentYearMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IE", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

// Monthly VAT auto-email status + manual trigger. The cron fires on the
// 1st at 07:00 Europe/Dublin for the month that just closed; this card
// surfaces the last attempt and lets the admin send any month on demand.
const VatSummaryEmailCard = ({ month, monthLabel }) => {
  const [runs, setRuns] = React.useState([]);
  const [sending, setSending] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices/admin/vat-summary/email-runs`, {
        params: { limit: 12 },
      });
      setRuns(res.data.runs || []);
    } catch (e) { /* silent — card still renders the send button */ }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const last = runs[0];
  const sentThisMonth = runs.find((r) => r.month === month && r.status === "sent");

  const send = async (force) => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/invoices/admin/vat-summary/email-now`, null, {
        params: { month, force },
      });
      if (res.data.status === "sent") {
        toast.success(`VAT pack for ${res.data.month} emailed to ${res.data.sent_to}`);
      } else if (res.data.status === "skipped") {
        toast.info(`${res.data.month} was already sent — use Re-send to force it`);
      } else {
        toast.error(res.data.error || "Send failed — check email logs");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to email VAT summary");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-slate-700 bg-slate-900/50 p-4 flex items-start justify-between gap-4 flex-wrap"
      data-testid="vat-summary-email-card"
    >
      <div className="min-w-[240px] flex-1">
        <div className="text-white font-semibold text-sm flex items-center gap-2">
          <Mail className="w-4 h-4 text-saturn-orange" />
          Accountant VAT pack
        </div>
        <p className="text-slate-400 text-xs mt-1">
          Auto-emailed on the 1st of each month at 07:00 (Dublin) for the month just
          closed — sales + purchases VAT PDFs attached.
        </p>
        <p className="text-xs mt-2" data-testid="vat-summary-email-last-run">
          {last ? (
            <span
              className={
                last.status === "sent"
                  ? "text-emerald-400"
                  : last.status === "skipped"
                  ? "text-slate-400"
                  : "text-red-400"
              }
            >
              Last attempt: {last.month} — {last.status}
              {last.sent_to ? ` → ${last.sent_to}` : ""}
              {last.error ? ` (${last.error})` : ""}
            </span>
          ) : (
            <span className="text-slate-500">
              No sends recorded yet on this environment.
            </span>
          )}
        </p>
      </div>
      <Button
        onClick={() => send(Boolean(sentThisMonth))}
        disabled={sending}
        variant="outline"
        className="border-slate-600 text-slate-200 hover:bg-slate-800"
        data-testid="email-vat-summary-btn"
      >
        {sending ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Mail className="w-4 h-4 mr-2" />
        )}
        {sentThisMonth ? `Re-send ${monthLabel}` : `Email ${monthLabel}`}
      </Button>
    </div>
  );
};


const InvoicesTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vatSummary, setVatSummary] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [downloadingSummary, setDownloadingSummary] = useState(false);

  // Filters
  const [month, setMonth] = useState(currentYearMonth());
  const [customer, setCustomer] = useState("");
  const [status, setStatus] = useState("any");
  const [debouncedCustomer, setDebouncedCustomer] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCustomer(customer.trim()), 300);
    return () => clearTimeout(t);
  }, [customer]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (month) params.month = month;
      if (debouncedCustomer) params.customer = debouncedCustomer;
      if (status && status !== "any") params.status = status;

      const [listRes, summaryRes] = await Promise.all([
        axios.get(`${API}/invoices/admin/list`, { params }),
        month
          ? axios.get(`${API}/invoices/admin/vat-summary`, { params: { month } })
          : Promise.resolve({ data: null }),
      ]);
      setInvoices(listRes.data.invoices || []);
      setVatSummary(summaryRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [month, debouncedCustomer, status]);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (inv) => {
    setResendingId(inv.invoice_id);
    try {
      const r = await axios.post(`${API}/invoices/${inv.invoice_id}/resend`);
      toast.success(`Invoice ${inv.invoice_number} re-sent to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend");
    } finally {
      setResendingId(null);
    }
  };

  const monthOptions = useMemo(() => {
    // 24 rolling months back from today
    const opts = [];
    const d = new Date();
    d.setDate(1);
    for (let i = 0; i < 24; i++) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const label = d.toLocaleDateString("en-IE", { month: "long", year: "numeric" });
      opts.push({ value: `${y}-${m}`, label });
      d.setMonth(d.getMonth() - 1);
    }
    return opts;
  }, []);

  const downloadMonthlySummaryPdf = async () => {
    setDownloadingSummary(true);
    try {
      const res = await axios.get(
        `${API}/invoices/admin/vat-summary/pdf`,
        { params: { month }, responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `saturn-styling-vat-summary-${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Monthly summary PDF downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate summary PDF");
    } finally {
      setDownloadingSummary(false);
    }
  };

  const monthLabel =
    monthOptions.find((o) => o.value === month)?.label || month;

  return (
    <div data-testid="invoices-tab">
      <Tabs defaultValue="sales" className="space-y-6">
        <TabsList className="bg-slate-900/60 border border-slate-700 h-auto p-1">
          <TabsTrigger
            value="sales"
            className="data-[state=active]:bg-saturn-orange data-[state=active]:text-white text-slate-300 gap-2 px-4 py-2"
            data-testid="invoices-subtab-sales"
          >
            <Receipt className="w-4 h-4" />
            Sales Invoices
          </TabsTrigger>
          <TabsTrigger
            value="purchases"
            className="data-[state=active]:bg-saturn-orange data-[state=active]:text-white text-slate-300 gap-2 px-4 py-2"
            data-testid="invoices-subtab-purchases"
          >
            <ShoppingCart className="w-4 h-4" />
            Purchases
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="space-y-6 mt-0" data-testid="invoices-sales-panel">
      {/* Header row — month label + download PDF action */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-white font-semibold text-lg tracking-tight">
            Invoicing — {monthLabel}
          </h3>
          <p className="text-slate-400 text-xs">
            Line items and totals for accountant hand-off
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
        <Button
          onClick={downloadMonthlySummaryPdf}
          disabled={downloadingSummary}
          className="bg-saturn-orange hover:bg-saturn-orange/80 text-white"
          data-testid="download-monthly-summary-pdf-btn"
        >
          {downloadingSummary ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileDown className="w-4 h-4 mr-2" />
          )}
          Sales VAT PDF
        </Button>
        </div>
      </div>

      {/* Unclassified-VAT status card — surfaces the count of already-costed
          units still missing a vat_treatment, and points to the bulk
          classifier in the Stock Inventory tab. */}
      <UnclassifiedVatBanner />

      {/* Monthly VAT pack auto-email — status + manual trigger */}
      <VatSummaryEmailCard month={month} monthLabel={monthLabel} />

      {/* VAT Summary Cards */}
      {vatSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="vat-summary">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-lg p-4">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Invoices this month</p>
            <p className="text-white text-3xl font-bold mt-1">{vatSummary.invoice_count}</p>
            <p className="text-slate-500 text-xs mt-1">{monthLabel}</p>
          </div>
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-lg p-4">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Net (ex-VAT)</p>
            <p className="text-white text-3xl font-bold mt-1">€{vatSummary.net_total.toFixed(2)}</p>
            <p className="text-slate-500 text-xs mt-1">Sum of net line items</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/40 rounded-lg p-4">
            <p className="text-amber-300 text-xs uppercase tracking-wider">VAT collected</p>
            <p className="text-white text-3xl font-bold mt-1" data-testid="vat-collected">
              €{vatSummary.vat_total.toFixed(2)}
            </p>
            <p className="text-amber-300 text-xs mt-1">
              @ {(vatSummary.vat_rate * 100).toFixed(1)}% — for your accountant
            </p>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-lg p-4">
            <p className="text-green-300 text-xs uppercase tracking-wider">Gross total</p>
            <p className="text-white text-3xl font-bold mt-1">€{vatSummary.gross_total.toFixed(2)}</p>
            <p className="text-green-300 text-xs mt-1">Revenue including VAT</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-slate-300 text-xs mb-1 block flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Month
            </Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="invoice-month-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {monthOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-slate-300 text-xs mb-1 block flex items-center gap-1">
              <Search className="w-3 h-3" /> Customer (name or email)
            </Label>
            <Input
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              placeholder="Start typing..."
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="invoice-customer-filter"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="invoice-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">All statuses</SelectItem>
                <SelectItem value="paid_in_full">Paid in full</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="border-slate-600 text-slate-300"
            data-testid="invoices-refresh-btn"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900/50 border border-slate-700 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-saturn-orange animate-spin" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16" data-testid="invoices-empty">
            <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No invoices found for the selected filters.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400">Invoice #</TableHead>
                <TableHead className="text-slate-400">Issued</TableHead>
                <TableHead className="text-slate-400">Customer</TableHead>
                <TableHead className="text-slate-400">Vehicle</TableHead>
                <TableHead className="text-slate-400 text-right">Net</TableHead>
                <TableHead className="text-slate-400 text-right">VAT</TableHead>
                <TableHead className="text-slate-400 text-right">Total</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow
                  key={inv.invoice_id}
                  className="border-slate-800 hover:bg-slate-800/50"
                  data-testid={`invoice-row-${inv.invoice_number}`}
                >
                  <TableCell className="font-mono text-saturn-orange text-sm">
                    {inv.invoice_number}
                  </TableCell>
                  <TableCell className="text-slate-300">{formatDate(inv.issued_at)}</TableCell>
                  <TableCell>
                    <div className="text-white text-sm">{inv.customer_name || "—"}</div>
                    <div className="text-slate-500 text-xs">{inv.customer_email}</div>
                  </TableCell>
                  <TableCell className="text-slate-300 font-mono text-xs">
                    {inv.vehicle_reg || "—"}
                  </TableCell>
                  <TableCell className="text-slate-300 text-right">€{inv.subtotal_net.toFixed(2)}</TableCell>
                  <TableCell className="text-amber-400 text-right">€{inv.vat_amount.toFixed(2)}</TableCell>
                  <TableCell className="text-white font-bold text-right">€{inv.total.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge className={
                      inv.status === "paid_in_full"
                        ? "bg-green-500/20 text-green-400 border-green-500/40"
                        : "bg-slate-700 text-slate-300"
                    }>
                      {inv.status === "paid_in_full" ? "Paid" : inv.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-slate-300 hover:text-white hover:bg-slate-700"
                        onClick={() => window.open(`/invoice/${inv.invoice_id}`, "_blank")}
                        title="Open hosted invoice"
                        data-testid={`invoice-view-${inv.invoice_number}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-slate-300 hover:text-white hover:bg-slate-700"
                        onClick={() => window.open(`${API}/invoices/${inv.invoice_id}/pdf`, "_blank")}
                        title="Download PDF"
                        data-testid={`invoice-download-${inv.invoice_number}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-saturn-orange hover:text-orange-300 hover:bg-orange-500/10"
                        onClick={() => handleResend(inv)}
                        disabled={resendingId === inv.invoice_id}
                        title="Re-send to customer"
                        data-testid={`invoice-resend-${inv.invoice_number}`}
                      >
                        {resendingId === inv.invoice_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {invoices.length > 0 && vatSummary && (
        <div className="flex items-center justify-between text-slate-400 text-sm bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3">
          <span className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-saturn-orange" />
            Showing <strong className="text-white">{invoices.length}</strong> invoice(s)
            {month && <> for <strong className="text-white">{monthLabel}</strong></>}
          </span>
          <span className="text-xs">
            Hand these totals to your accountant — the VAT figure is what you owe on the reporting period.
          </span>
        </div>
      )}
        </TabsContent>

        <TabsContent value="purchases" className="mt-0" data-testid="invoices-purchases-panel">
          <PurchasesSubTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InvoicesTab;
